import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import "./PortfolioCategoryPage.scss";
import { Header } from "../../header/Header";
import Footer from "../../footer/Footer";
import {
  PORTFOLIO_CATEGORIES,
  getCategory,
  getHeroForCategory,
  getHeroPortraitForCategory,
  getItemsForCategory,
} from "./Constant";
import { useEntrance } from "../../../motion/useEntrance";
import { useParallax } from "../../../motion/useParallax";
import { useScrollReveal } from "../../../motion/useScrollReveal";
import SmoothScroll from "../../../motion/SmoothScroll";
import { usePagePreload } from "../../../preload/usePagePreload";
import { preloadImages } from "../../../preload/imageCache";
import { getPortfolioCategoryImages } from "../../../preload/pageAssets";
import PageSkeleton from "../../../preload/PageSkeleton";
import YouTubeLite from "./YouTubeLite";

const PortfolioCategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();

  const activeCategory = getCategory(category);

  // Index of the asset currently featured in the carousel.
  const [activeIndex, setActiveIndex] = useState(0);

  const items = getItemsForCategory(category);

  const { ready } = usePagePreload(getPortfolioCategoryImages(category));

  // Reset the carousel + scroll to top whenever the category changes. Runs
  // before the reveal hooks below so their ScrollTrigger setup sees the page
  // already back at the top, not the pre-navigation scroll position.
  useEffect(() => {
    setActiveIndex(0);
    window.scrollTo({ top: 0 });
  }, [category]);

  // Hero background drifts (parallax) as the page scrolls away; hero copy
  // rises + fades in on load; the tabs + carousel section below reveals as
  // it scrolls into view. Re-run on every category change since the route
  
  const heroMediaRef = useParallax<HTMLDivElement>({ amount: 120, deps: [category, ready] });
  const heroContentRef = useEntrance<HTMLDivElement>({
    selector: ":scope > *",
    deps: [category, ready],
  });
  const bodyRevealRef = useScrollReveal<HTMLDivElement>({
    selector: ".portfolio-page-body",
    stagger: 0.12,
    deps: [category, ready],
  });

  const scrollToCarousel = useCallback(() => {
    document
      .getElementById("portfolio-carousel-section")
      ?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const showPrev = useCallback(() => {
    setActiveIndex((current) => (current - 1 + items.length) % items.length);
  }, [items.length]);

  const showNext = useCallback(() => {
    setActiveIndex((current) => (current + 1) % items.length);
  }, [items.length]);

  // Keyboard arrow controls for the carousel.
  useEffect(() => {
    if (items.length <= 1) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") showPrev();
      else if (event.key === "ArrowRight") showNext();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [items.length, showPrev, showNext]);

  // Unknown slug → send the user back to the Home portfolio section.
  if (!activeCategory) {
    return <Navigate to="/home" state={{ scrollTo: "art-portfolio" }} replace />;
  }

  if (!ready) {
    return <PageSkeleton cards={items.length || 3} />;
  }

  const heroImage = getHeroForCategory(category);
  const heroPortraitImage = getHeroPortraitForCategory(category);
  const activeItem = items[activeIndex];

  return (
    <SmoothScroll>
      <div className="portfolio-page">
        <Header />

        <div
          className="portfolio-page-hero"
          role="img"
          aria-label={`${activeCategory.label} portfolio hero`}
        >
          <div
            className="portfolio-page-hero-media"
            ref={heroMediaRef}
            style={{
              "--hero-bg": `url(${heroImage})`,
              "--hero-bg-portrait": `url(${heroPortraitImage})`,
            } as React.CSSProperties}
          />
          <div className="portfolio-page-hero-overlay" />
          <div className="portfolio-page-hero-content" ref={heroContentRef}>
            <h1 className="portfolio-page-hero-title">{activeCategory.label}</h1>
          </div>

          <button
            type="button"
            className="portfolio-page-hero-scroll"
            onClick={scrollToCarousel}
            aria-label="Scroll down to portfolio carousel"
          >
            <span className="portfolio-page-hero-scroll-label">Scroll</span>
            <span className="portfolio-page-hero-scroll-icon" aria-hidden="true" />
            <span className="portfolio-page-hero-scroll-chevrons" aria-hidden="true">
              <span className="portfolio-page-hero-scroll-chevron" />
              <span className="portfolio-page-hero-scroll-chevron" />
            </span>
          </button>
        </div>

        <div ref={bodyRevealRef}>
          <nav className="portfolio-page-tabs" aria-label="Portfolio categories">
            <div className="portfolio-page-tabs-inner">
              {PORTFOLIO_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={
                    cat.id === activeCategory.id
                      ? "portfolio-page-tab portfolio-page-tab-active"
                      : "portfolio-page-tab"
                  }
                  aria-current={cat.id === activeCategory.id ? "page" : undefined}
                  onMouseEnter={() => preloadImages(getPortfolioCategoryImages(cat.id))}
                  onFocus={() => preloadImages(getPortfolioCategoryImages(cat.id))}
                  onClick={() => navigate(`/portfolio/${cat.id}`)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </nav>

          <section
            id="portfolio-carousel-section"
            className="portfolio-page-body"
            aria-label={activeCategory.label}
          >
            <div className="portfolio-page-body-inner">
              {items.length === 0 || !activeItem ? (
                <p className="portfolio-page-empty">
                  Work for this category is coming soon.
                </p>
              ) : (
                <div className="portfolio-carousel">
                  {items.length > 1 && (
                    <button
                      type="button"
                      className="portfolio-carousel-nav portfolio-carousel-prev"
                      onClick={showPrev}
                      aria-label="Previous"
                    >
                      &#10094;
                    </button>
                  )}

                  <figure className="portfolio-carousel-stage">
                    {activeItem.youtubeId ? (
                      <YouTubeLite
                        key={activeItem.id}
                        videoId={activeItem.youtubeId}
                        title={activeItem.title}
                      />
                    ) : (
                      <img
                        key={activeItem.id}
                        src={activeItem.image}
                        alt={activeItem.title}
                        className="portfolio-carousel-img"
                      />
                    )}
                  </figure>

                  {items.length > 1 && (
                    <button
                      type="button"
                      className="portfolio-carousel-nav portfolio-carousel-next"
                      onClick={showNext}
                      aria-label="Next"
                    >
                      &#10095;
                    </button>
                  )}
                </div>
              )}

              {items.length > 1 && (
                <div
                  className="portfolio-carousel-thumbs"
                  role="tablist"
                  aria-label={`${activeCategory.label} thumbnails`}
                >
                  {items.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={index === activeIndex}
                      className={
                        index === activeIndex
                          ? "portfolio-carousel-thumb portfolio-carousel-thumb-active"
                          : "portfolio-carousel-thumb"
                      }
                      onClick={() => setActiveIndex(index)}
                      aria-label={`Show ${item.title}`}
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className="portfolio-carousel-thumb-img"
                      />
                      {item.youtubeId && (
                        <span className="portfolio-carousel-thumb-play" aria-hidden="true">▶</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </SmoothScroll>
  );
};

export default PortfolioCategoryPage;
